import React, { useEffect, useState } from "react";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, StatusBadge } from "@/components/ui-parts";
import { CheckCircle2, XCircle, Sparkles, MessageSquare, User, ArrowRight, Info, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const KIND_LABELS = {
  blur: "Unscharfes Foto",
  handwriting: "Handschriftliche Angaben",
  category: "Untypische Kategorie",
  weekend: "Wochenende",
  nighttime: "Ungewöhnliche Uhrzeit",
  vehicle_plate: "Fehlendes Kennzeichen",
  vehicle_new: "Neues Fahrzeug",
  missing_fields: "Fehlende Pflichtfelder",
  freeform_note: "Freitext-Antwort",
};

const KIND_ICON = {
  blur: "📸",
  handwriting: "✍️",
  category: "❓",
  weekend: "📅",
  nighttime: "🌙",
  vehicle_plate: "🚗",
  vehicle_new: "🚗",
  missing_fields: "🧾",
  freeform_note: "💬",
};

function DialogCard({ d, onDecide }) {
  const [saving, setSaving] = useState(false);
  const decide = async (decision) => {
    setSaving(true);
    try {
      await onDecide(d.id, { decision });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-white border border-[#E6E6E4] rounded-md p-5 space-y-3" data-testid={`ai-dialog-${d.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="text-2xl">{KIND_ICON[d.kind] || "🤖"}</div>
          <div>
            <div className="font-display font-bold text-[#1A1A1A]">
              {KIND_LABELS[d.kind] || d.kind}
            </div>
            <div className="text-xs text-[#5C5E60] flex items-center gap-1.5 mt-0.5">
              <User className="h-3 w-3" /> {d.member_name || "Unbekannter Mitarbeiter"} · {fmtDate(d.created_at)}
            </div>
          </div>
        </div>
        <StatusBadge variant={d.severity === "warn" ? "warning" : "info"}>
          {d.severity === "warn" ? "Verdacht" : "Info"}
        </StatusBadge>
      </div>

      {d.receipt_preview && (
        <div className="bg-[#F9F9F8] border border-[#E6E6E4] rounded p-3 text-xs">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-[#1A1A1A]">{d.receipt_preview.vendor || "Unbekannter Lieferant"}</div>
              <div className="text-[#5C5E60] mt-0.5">
                {fmtDate(d.receipt_preview.date)} · {fmtEUR(d.receipt_preview.total_amount)} · {d.receipt_preview.category || d.receipt_preview.receipt_type || "keine Kategorie"}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="bg-[#EAF4FA] border-l-2 border-[#1A4971] rounded-r px-3 py-2 text-sm">
          <div className="text-[10px] uppercase tracking-wider text-[#1A4971] font-semibold mb-1 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Frage der KI
          </div>
          <div className="text-[#1A1A1A]" data-testid={`ai-question-${d.id}`}>{d.question}</div>
        </div>
        {d.answer ? (
          <div className="bg-[#E4F0E8] border-l-2 border-[#18322B] rounded-r px-3 py-2 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-[#18322B] font-semibold mb-1 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> Antwort des Mitarbeiters
            </div>
            <div className="text-[#1A1A1A]" data-testid={`ai-answer-${d.id}`}>{d.answer}</div>
          </div>
        ) : (
          <div className="text-xs text-[#5C5E60] italic pl-3">Noch keine Antwort erhalten…</div>
        )}
      </div>

      {d.status === "pending" && (
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={() => decide("reject")}
            disabled={saving}
            data-testid={`ai-reject-${d.id}`}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md text-[#B31D1D] hover:bg-[#FCE8E8] transition-colors">
            <XCircle className="h-4 w-4" /> Ablehnen
          </button>
          <PrimaryButton onClick={() => decide("approve")} disabled={saving} data-testid={`ai-approve-${d.id}`}>
            <CheckCircle2 className="h-4 w-4" /> Bestätigen &amp; lernen
          </PrimaryButton>
        </div>
      )}
      {d.status !== "pending" && (
        <div className="text-xs text-[#5C5E60]">
          {d.status === "approved" ? "✅ Bestätigt" : "❌ Abgelehnt"}
          {d.reviewed_at ? ` · ${fmtDate(d.reviewed_at)}` : ""}
        </div>
      )}

      {d.similar_memories && d.similar_memories.length > 0 && (
        <div className="pt-3 border-t border-[#E6E6E4]">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold mb-2 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Ähnliche Fälle aus dem Erfahrungsgedächtnis
          </div>
          <div className="space-y-1.5">
            {d.similar_memories.map((m, i) => (
              <div key={i} className="text-xs bg-[#F9F9F8] rounded px-2.5 py-1.5 border border-[#E6E6E4]">
                <span className="inline-block text-[9px] px-1.5 py-0.5 mr-1.5 rounded bg-[#EAF4FA] text-[#1A4971] font-mono">
                  {m.kind === "context" ? "DOSSIER" : "GELERNT"} · {m.score}
                </span>
                <span className="text-[#1A1A1A]">{m.text.length > 240 ? m.text.slice(0, 240) + "…" : m.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AiReview() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [memoryStats, setMemoryStats] = useState({ total: 0 });
  const [memoryQuery, setMemoryQuery] = useState("");
  const [memoryResults, setMemoryResults] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: s }, { data: mem }] = await Promise.all([
        api.get("/ai-dialogs", { params: { status: statusFilter } }),
        api.get("/ai-settings"),
        api.get("/ai-memory", { params: { limit: 1 } }).catch(() => ({ data: { total: 0 } })),
      ]);
      setItems(data);
      setSettings(s);
      setMemoryStats(mem);
    } catch {
      toast.error("KI-Dialoge konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const testMemory = async () => {
    if (!memoryQuery.trim()) return;
    try {
      const { data } = await api.get("/ai-memory/retrieve", { params: { query: memoryQuery, top_k: 5 } });
      setMemoryResults(data.results || []);
    } catch (err) {
      toast.error("Abfrage fehlgeschlagen: " + (err.response?.data?.detail || err.message));
    }
  };

  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const uploadReference = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (uploadTitle.trim()) fd.append("title", uploadTitle.trim());
    setUploading(true);
    try {
      const { data } = await api.post("/ai-memory/upload-reference", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`„${data.title}" indiziert: ${data.chunks_indexed} Abschnitte aus ${data.pages} Seiten`);
      setUploadTitle("");
      load();
    } catch (err) {
      toast.error("Upload fehlgeschlagen: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const decide = async (id, body) => {
    try {
      await api.post(`/ai-dialogs/${id}/decide`, body);
      toast.success(body.decision === "approve" ? "Freigegeben – die KI lernt aus dieser Entscheidung." : "Abgelehnt");
      load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const switchMode = async (mode) => {
    try {
      await api.post("/ai-settings", { mode });
      toast.success(mode === "suspicious_only" ? "Dauerbetrieb aktiviert" : "Prüfmodus aktiviert");
      load();
    } catch { toast.error("Umschalten fehlgeschlagen"); }
  };

  return (
    <div>
      <PageHeader
        title="KI-Prüfung"
        subtitle="Klärungsdialoge zwischen dem KI-Assistenten und den Mitarbeitern. Deine Entscheidung schult die KI."
        actions={
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="ai-status-filter"
              className="text-sm px-3 py-2 border border-[#E6E6E4] rounded-md bg-white">
              <option value="pending">Offen</option>
              <option value="approved">Bestätigt</option>
              <option value="rejected">Abgelehnt</option>
              <option value="all">Alle</option>
            </select>
          </div>
        }
      />

      <div className="px-8 py-8 space-y-6">
        {settings && (
          <div className="bg-white border border-[#E6E6E4] rounded-md p-4 flex items-center justify-between" data-testid="ai-mode-panel">
            <div className="flex items-center gap-3 text-sm">
              <Sparkles className="h-5 w-5 text-[#18322B]" strokeWidth={1.75} />
              <div>
                <div className="font-semibold text-[#1A1A1A]">
                  Modus: {settings.mode === "review_all" ? "Alles prüfen (Lernphase)" : "Dauerbetrieb (nur Verdachtsfälle)"}
                </div>
                <div className="text-xs text-[#5C5E60] mt-0.5">
                  {settings.pending_count} offen · {settings.approved_count} bereits bestätigt · {memoryStats.total} Erinnerungen im Gedächtnis
                </div>
              </div>
            </div>
            {settings.suggested_switch_to_suspicious_only ? (
              <PrimaryButton onClick={() => switchMode("suspicious_only")} data-testid="switch-to-suspicious">
                <ArrowRight className="h-4 w-4" /> In Dauerbetrieb wechseln
              </PrimaryButton>
            ) : settings.mode === "suspicious_only" ? (
              <SecondaryButton onClick={() => switchMode("review_all")} data-testid="switch-to-review">
                Zurück zur Lernphase
              </SecondaryButton>
            ) : (
              <div className="text-xs text-[#5C5E60] max-w-xs text-right flex items-start gap-1">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Nach 20 bestätigten Dialogen kannst du in den Dauerbetrieb wechseln.
              </div>
            )}
          </div>
        )}

        <div className="bg-white border border-[#E6E6E4] rounded-md p-4" data-testid="memory-upload-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold flex items-center gap-1">
              <FileText className="h-3 w-3" /> Referenz-Dokument hochladen
            </div>
            <div className="text-[11px] text-[#5C5E60]">
              PDF oder Textdatei · max. 100 MB
            </div>
          </div>
          <div className="text-xs text-[#5C5E60] mb-3">
            Lade eine fertige Monatsabgabe, Beispielrechnung, Kontierungs-Richtlinie oder anderes Referenzmaterial hoch. Der Text wird zerlegt und die KI zieht ihn bei jedem neuen Beleg heran.
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="Titel (optional, z.B. 'Musterabgabe Goldenbed Januar 2026')"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              data-testid="reference-title"
              className="flex-1 min-w-[240px] px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]"
            />
            <label className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md cursor-pointer transition-colors ${uploading ? "bg-[#F3F3F1] text-[#5C5E60] cursor-not-allowed" : "bg-[#18322B] hover:bg-[#21453B] text-white"}`} data-testid="reference-upload-label">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Wird verarbeitet…" : "Datei wählen"}
              <input
                type="file"
                className="hidden"
                accept=".pdf,.txt,.md,.csv"
                disabled={uploading}
                onChange={(e) => uploadReference(e.target.files?.[0])}
                data-testid="reference-file"
              />
            </label>
          </div>
        </div>

        <div className="bg-white border border-[#E6E6E4] rounded-md p-4" data-testid="memory-search-panel">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold mb-2 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Erfahrungsgedächtnis testen
          </div>
          <div className="flex gap-2">
            <input
              value={memoryQuery}
              onChange={(e) => setMemoryQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && testMemory()}
              placeholder="z.B. Baumaterial für Baustelle Wolfsburg"
              data-testid="memory-query"
              className="flex-1 px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]"
            />
            <PrimaryButton onClick={testMemory} data-testid="memory-search">
              Abfragen
            </PrimaryButton>
          </div>
          {memoryResults && (
            <div className="mt-3 space-y-1.5">
              {memoryResults.length === 0 ? (
                <div className="text-xs text-[#5C5E60] italic">Keine passenden Erinnerungen gefunden.</div>
              ) : (
                memoryResults.map((r, i) => (
                  <div key={i} className="text-xs bg-[#F9F9F8] rounded px-2.5 py-1.5 border border-[#E6E6E4]">
                    <span className="inline-block text-[9px] px-1.5 py-0.5 mr-1.5 rounded bg-[#EAF4FA] text-[#1A4971] font-mono">
                      {r.kind === "context" ? "DOSSIER" : "GELERNT"} · {r.score}
                    </span>
                    <span className="text-[#1A1A1A]">{r.text.length > 320 ? r.text.slice(0, 320) + "…" : r.text}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E6E6E4] rounded-md p-8 text-center text-sm text-[#5C5E60]">
            {statusFilter === "pending"
              ? "Keine offenen KI-Rückfragen. 🎉"
              : "Keine Einträge in dieser Ansicht."}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {items.map((d) => <DialogCard key={d.id} d={d} onDecide={decide} />)}
          </div>
        )}
      </div>
    </div>
  );
}
